export const OTA_BUSTER_ENTRY = {
  title: 'Guests booked via OTA'
}

export const EMPTY_STEP_METRICS = {
  guests: 0,
  emailsSent: 0,
  clickThroughRate: 0
}

/**
 * Step definitions for the Campaign Flow UI.
 * Timing for pre-check-in steps is editable; event-based steps stay fixed labels.
 * Each step is independently enableable (unlike Site Abandonment chain rules).
 * Previews here are only the fallback when the hotel has no saved message template yet.
 */
export const OTA_BUSTER_CAMPAIGN_STEPS = [
  {
    id: 'just-booked',
    contentCategory: 'OtaBusterJustBooked',
    timingKind: 'event',
    timingLabel: 'Just after booking',
    title: 'Just Booked',
    iconColor: '#7c5cbf',
    preview:
      "Hi {first}, thanks for booking {hotel}! Here's your confirmation and a few quick details.",
    defaultDaysBeforeCheckIn: null
  },
  {
    id: 'pre-check-in',
    contentCategory: 'OtaBusterPreCheckIn',
    timingKind: 'daysBeforeCheckIn',
    timingLabel: '3 days before check-in',
    title: 'Pre-check-in email',
    iconColor: '#2f9e74',
    preview:
      'Hi {first}, your stay at {hotel} is just 3 days away. Confirm a few details before you arrive.',
    defaultDaysBeforeCheckIn: 3
  },
  {
    id: 'pre-check-in-reminder',
    contentCategory: 'OtaBusterPreCheckInReminder',
    timingKind: 'daysBeforeCheckIn',
    timingLabel: '1 day before check-in',
    title: 'Pre-check-in reminder',
    iconColor: '#d4a017',
    preview:
      "Hi {first}, we can't wait to welcome you tomorrow. Here's what to expect at arrival.",
    defaultDaysBeforeCheckIn: 1
  },
  {
    id: 'post-check-in',
    contentCategory: 'OtaBusterPostCheckIn',
    timingKind: 'event',
    timingLabel: 'Just after check-in',
    title: 'Post-check-in email',
    iconColor: '#2f6fed',
    preview:
      "Hi {first}, welcome to {hotel}! Here's everything you need for a great stay.",
    defaultDaysBeforeCheckIn: null
  },
  {
    id: 'post-checkout',
    contentCategory: 'OtaBusterPostCheckout',
    timingKind: 'event',
    timingLabel: 'Just after checkout',
    title: 'Post-checkout email',
    iconColor: '#d4537e',
    preview:
      "Hi {first}, thank you for staying with us. We'd love to hear about your experience.",
    defaultDaysBeforeCheckIn: null
  }
]

export const OTA_BUSTER_CONTENT_CATEGORIES = OTA_BUSTER_CAMPAIGN_STEPS.map(
  step => step.contentCategory
)

export function getStepById(stepId) {
  return OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.id === stepId) || null
}

export function getStepByCategory(category) {
  return (
    OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.contentCategory === category) || null
  )
}

export function formatTimingLabel(step, daysBeforeCheckIn) {
  if (!step) return ''
  if (step.timingKind !== 'daysBeforeCheckIn') {
    return step.timingLabel
  }

  const days = Number(daysBeforeCheckIn) || step.defaultDaysBeforeCheckIn || 1
  if (days === 1) {
    return '1 day before check-in'
  }
  return `${days} days before check-in`
}

export function daysToHours(days) {
  return Math.max(1, Number(days) || 1) * 24
}

export function hoursToDays(hours, fallbackDays) {
  if (hours == null || hours <= 0) {
    return fallbackDays
  }
  if (hours % 24 === 0) {
    return hours / 24
  }
  return Math.max(1, Math.round(hours / 24))
}

export function interpolateCampaignPreview(template, { firstName, hotelName } = {}) {
  return (template || '')
    .replace(/\{first\}/g, firstName || 'Joe')
    .replace(/\{hotel\}/g, hotelName || 'the hotel')
}

export function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US')
}

export function toPercent(rate) {
  return Math.round((Number(rate) || 0) * 100)
}

/**
 * Maps the analytics payload to per-step metrics keyed by step id.
 * The API serializes `category` as the numeric enum value, so the step slug is the
 * reliable join key; `category` is still honoured for payloads that send the enum name.
 * Steps without reporting data yet fall back to zeros instead of placeholder numbers.
 */
export function buildStepMetrics(analytics) {
  const byStep = {}
  const byCategory = {}
  ;(analytics?.steps || []).forEach(item => {
    if (item.step) {
      byStep[item.step] = item
    }
    byCategory[item.category] = item
  })

  return OTA_BUSTER_CAMPAIGN_STEPS.reduce((acc, step) => {
    const item = byStep[step.id] || byCategory[step.contentCategory]

    acc[step.id] = item
      ? {
          guests: item.guests || 0,
          emailsSent: item.emailsSent || 0,
          clickThroughRate: toPercent(item.clickThroughRate)
        }
      : { ...EMPTY_STEP_METRICS }

    return acc
  }, {})
}
